/**
 * InjectionMonitor - Conecta InjectionDetector al sensor (OnLoad + interceptación de requests).
 *
 * Incluye dedupe por sesión, rate limit, sanitización de evidencia y severidad por tipo.
 * Solo observa; no bloquea requests ni altera comportamiento del usuario.
 */

import { InjectionDetector } from '../security/injection-detector.js';
import { SSRFDetector } from './ssrf-detector.js';
import { OpenRedirectDetector } from './open-redirect-detector.js';
import { NoSQLDetector } from './nosql-detector.js';
import { PrototypePollutionDetector } from './prototype-pollution-detector.js';
import { sanitizeEvidence, sanitizeUrl } from '../security/evidence-sanitizer.js';
import { ADR_CONSTANTS } from '../core/constants.js';
import SeverityCalculator from '../core/severity-calculator.js';
import { EVENT_CATALOG } from '../core/event-catalog.js';

/**
 * Normaliza body para análisis: JSON string, form-urlencoded string, texto plano o ''.
 */
function normalizeBody(body, contentType) {
	if (body === undefined || body === null) return '';
	if (typeof body === 'string') return body;
	if (typeof body === 'object') {
		if (body instanceof URLSearchParams) return body.toString();
		if (body instanceof FormData) {
			const pairs = [];
			for (const [k, v] of body.entries()) {
				pairs.push(encodeURIComponent(k) + '=' + encodeURIComponent(typeof v === 'string' ? v : (v && v.name) ? v.name : ''));
			}
			return pairs.join('&');
		}
		try {
			return JSON.stringify(body);
		} catch (_) {
			return String(body);
		}
	}
	return String(body);
}

function headersToObject(headers) {
	if (!headers) return {};
	if (headers instanceof Headers) {
		const o = {};
		headers.forEach((v, k) => { o[k] = v; });
		return o;
	}
	return typeof headers === 'object' ? headers : {};
}

/**
 * Normaliza URL para clave de dedupe: origin + pathname (sin query/hash).
 * @param {string} url
 * @param {string} [base]
 * @returns {string}
 */
function normalizeUrlForDedupe(url, base) {
	if (!url || typeof url !== 'string') return '';
	try {
		const parsed = base ? new URL(url, base) : new URL(url);
		return (parsed.origin || '') + (parsed.pathname || '/');
	} catch (_) {
		return url;
	}
}

/**
 * Mapea el tipo del detector al event_type que acepta el backend.
 */
function eventTypeForResult(type) {
	switch (type) {
		case 'PATH_TRAVERSAL': return 'path_traversal';
		case 'SQLI': return 'sql_injection';
		case 'RCE': return 'command_injection';
		case 'XSS': return ADR_CONSTANTS.EVENT_TYPES.XSS;
		case 'SSRF': return ADR_CONSTANTS.EVENT_TYPES.SSRF;
		case 'OPEN_REDIRECT': return ADR_CONSTANTS.EVENT_TYPES.OPEN_REDIRECT;
		case 'NOSQL': return ADR_CONSTANTS.EVENT_TYPES.NOSQL_INJECTION;
		case 'PROTOTYPE_POLLUTION': return ADR_CONSTANTS.EVENT_TYPES.PROTOTYPE_POLLUTION;
		default: return 'injection_attempt';
	}
}

/**
 * Mapea el tipo del detector a la clave del catálogo (EVENT_CATALOG) para SeverityCalculator.
 */
function catalogKeyForType(type) {
	switch (type) {
		case 'SQLI': return 'sql_injection';
		case 'RCE': return 'command_injection';
		case 'PATH_TRAVERSAL': return 'path_traversal';
		case 'XSS': return 'xss';
		case 'SSRF': return 'ssrf';
		case 'OPEN_REDIRECT': return 'open_redirect';
		case 'NOSQL': return 'nosql_injection';
		case 'PROTOTYPE_POLLUTION': return 'prototype_pollution';
		default: return null;
	}
}

export class InjectionMonitor {
	constructor(severityCalculator = null) {
		this.detector = new InjectionDetector();
		this.ssrfDetector = new SSRFDetector();
		this.openRedirectDetector = new OpenRedirectDetector();
		this.nosqlDetector = new NoSQLDetector();
		this.protoPollutionDetector = new PrototypePollutionDetector();
		this.severityCalculator = severityCalculator || new SeverityCalculator(EVENT_CATALOG);
		this.transport = null;
		this.config = null;
		this._dedupeMap = null;
		this._rateLimitTimestamps = null;
		this._originalFetch = null;
		this._originalXHROpen = null;
		this._originalXHRSend = null;
		this._originalXHRSetRequestHeader = null;
		this._submitBound = null;
	}

	start(transport, config = null) {
		if (typeof window === 'undefined') return;
		this.transport = transport;
		this.config = config || {};
		this._dedupeMap = new Map();
		this._rateLimitTimestamps = [];
		this.runOnLoad();
		this.patchFetch();
		this.patchXHR();
		this.attachFormSubmit();
	}

	stop() {
		this.restoreFetch();
		this.restoreXHR();
		this.detachFormSubmit();
		this._dedupeMap = null;
		this._rateLimitTimestamps = null;
		this.transport = null;
		this.config = null;
	}

	_debug(msg) {
		const debug = this.config && (this.config.get('debug') || this.config.get('injectionDebug'));
		if (debug && typeof console !== 'undefined' && console.debug) {
			console.debug('[ADR Injection]', msg);
		}
	}

	_getOption(key, defaultValue) {
		if (!this.config || typeof this.config.get !== 'function') return defaultValue;
		const v = this.config.get(key);
		return v !== undefined && v !== null ? v : defaultValue;
	}

	/**
	 * Avoid duplicates events
	 */
	_isAdrEndpoint(url) {
		try {
			if (!url) return false;
			const urlString = typeof url === 'string' ? url : String(url);

			const endpoint = this.config && this.config.get ? this.config.get('endpoint') : null;
			const tokenEndpoint = this.config && this.config.get ? this.config.get('tokenEndpoint') : null;
			const scriptIntegrityEndpoint = this.config && this.config.get ? this.config.get('scriptIntegrityEndpoint') : null;

			const endpointsToIgnore = [endpoint, tokenEndpoint, scriptIntegrityEndpoint].filter(
				(v) => typeof v === 'string' && v.trim()
			);
			if (!endpointsToIgnore.length) return false;

			// Direct/substring match for relative endpoints
			for (const ep of endpointsToIgnore) {
				if (urlString === ep) return true;
				if (urlString.indexOf(ep) !== -1) return true;
			}

			// Absolute URL match
			if (typeof window !== 'undefined' && window.location && window.location.origin) {
				for (const ep of endpointsToIgnore) {
					try {
						const absEp = new URL(ep, window.location.origin).toString();
						const absUrl = new URL(urlString, window.location.origin).toString();
						if (absUrl === absEp) return true;
					} catch (_) {}
				}
			}

			return false;
		} catch (_) {
			return false;
		}
	}

	/**
	 * Dedupe: key = attack_type|variant|method|normalizedUrl|signature (truncated evidence).
	 * Retorna true si ya está en ventana TTL (no enviar de nuevo).
	 */
	_isDedupe(result, normalizedUrl, method, evidenceSnippet) {
		const ttlMs = this._getOption('injectionDedupeTtlMs', 60000);
		const now = Date.now();
		const key = [
			result.type,
			result.variant,
			(method || '').toUpperCase(),
			normalizedUrl,
			(evidenceSnippet || '').substring(0, 64)
		].join('|');

		// Limpia entradas expiradas
		for (const [k, expiresAt] of this._dedupeMap.entries()) {
			if (expiresAt <= now) this._dedupeMap.delete(k);
		}

		if (this._dedupeMap.has(key)) {
			return true;
		}
		this._dedupeMap.set(key, now + ttlMs);
		return false;
	}

	/**
	 * Rate limit: max N eventos por minuto. Retorna true si se debe descartar.
	 */
	_isRateLimited() {
		const maxPerMin = this._getOption('injectionMaxPerMinute', 10);
		const now = Date.now();
		const oneMinAgo = now - 60000;
		this._rateLimitTimestamps = this._rateLimitTimestamps.filter(ts => ts > oneMinAgo);
		if (this._rateLimitTimestamps.length >= maxPerMin) {
			return true;
		}
		this._rateLimitTimestamps.push(now);
		return false;
	}

	/**
	 * Reporta solo si pasa dedupe y rate limit. Meta: attack_type, variant, url, method, location, evidence_redacted.
	 * No incluye headers/body crudos.
	 */
	report(result, url, method = '', location = '') {
		if (!this.transport || !result.detected) return;

		const maxLen = this._getOption('injectionEvidenceMaxLen', 256);
		const redactEnabled = this._getOption('injectionRedactEnabled', true);
		const { sanitized, wasRedacted } = sanitizeEvidence(result.evidence, { maxLen, redact: redactEnabled });

		const baseUrl = (typeof window !== 'undefined' && window.location && window.location.href) ? window.location.href : '';
		const normalizedUrl = normalizeUrlForDedupe(url || baseUrl, baseUrl);

		if (this._isDedupe(result, normalizedUrl, method, sanitized)) {
			this._debug('Dropped (dedupe): ' + result.type + ' ' + result.variant);
			return;
		}
		if (this._isRateLimited()) {
			this._debug('Dropped (rate limit)');
			return;
		}

		const fullUrl = url || baseUrl;
		const eventType = eventTypeForResult(result.type);
		const catalogKey = catalogKeyForType(result.type);

		let severity = ADR_CONSTANTS.SEVERITY.HIGH;
		let cvss_score;
		let cvss_vector;
		let cwe_id;
		let mitre_technique;
		let owasp_category;

		if (catalogKey && this.severityCalculator) {
			const context = {
				url: fullUrl,
				method: method || '',
				evidence: sanitized
			};
			const calcResult = this.severityCalculator.calculate(catalogKey, result.variant, context);
			severity = calcResult.severity;
			cvss_score = calcResult.cvssScore;
			cvss_vector = calcResult.cvssVector;
			cwe_id = calcResult.cweId;
			mitre_technique = calcResult.mitreTechnique;
			owasp_category = calcResult.owaspCategory;
		}

		const originUrl = (typeof window !== 'undefined' && window.location && window.location.href) ? window.location.href : '';

		// Sanitize URLs before forwarding — strips values from sensitive query
		// params (token, session, api_key, etc.) so customer URL secrets don't
		// leak to the IRIS endpoint on every detector hit. Added 2026-05-25
		// per security-review-iris-scanners-2026-05-25.md finding #6.
		const meta = {
			attack_type: result.type,
			variant: result.variant,
			url: sanitizeUrl(fullUrl),
			method: method || '',
			location: location || '',
			evidence_redacted: wasRedacted,
			origin_url: sanitizeUrl(originUrl)
		};
		// Forward the detector's own match confidence ('high'|'medium') so the
		// backend/dashboard can distinguish a high-certainty match from a broader,
		// noisier one instead of showing every match at the same severity weight.
		// Purely additive to the existing flexible `meta` JSON — no backend change
		// needed. See US #<ADO-ticket> false-positive audit.
		if (result.confidence) meta.confidence = result.confidence;
		if (cvss_score !== undefined) meta.cvss_score = cvss_score;
		if (cvss_vector) meta.cvss_vector = cvss_vector;
		if (cwe_id) meta.cwe_id = cwe_id;
		if (mitre_technique) meta.mitre_technique = mitre_technique;
		if (owasp_category) meta.owasp_category = owasp_category;

		this.transport.enqueue(
			eventType,
			sanitized,
			severity,
			meta
		);
	}

	runOnLoad() {
		if (!window.location) return;
		const inputs = [
			window.location.href,
			window.location.search || '',
			window.location.hash || ''
		].filter(Boolean);
		for (const url of inputs) {
			const res = this.detector.analyzeOnLoad({ url });
			if (res.detected) {
				// Avoid duplicate XSS reporting
				if (res.type === 'XSS') {
					continue;
				}
				this.report(res, url, 'GET', 'onload');
				break;
			}
		}

		// Open redirect scan: check current page URL for redirect params pointing
		// outside the current origin. Runs separately so injection detection above
		// can still report even when a redirect param is also present.
		const currentOrigin = window.location.origin || '';
		const redirectRes = this.openRedirectDetector.analyze(window.location.href, currentOrigin);
		if (redirectRes.detected) {
			this.report(redirectRes, window.location.href, 'GET', 'onload');
		}

		// NoSQL injection: scan URL params for MongoDB operator bracket notation
		const nosqlRes = this.nosqlDetector.analyze({ url: window.location.href });
		if (nosqlRes.detected) {
			this.report(nosqlRes, window.location.href, 'GET', 'onload');
		}

		// Prototype pollution: scan URL params for __proto__ / constructor.prototype
		const protoRes = this.protoPollutionDetector.analyze({ url: window.location.href });
		if (protoRes.detected) {
			this.report(protoRes, window.location.href, 'GET', 'onload');
		}
	}

	patchFetch() {
		if (typeof fetch === 'undefined' || this._originalFetch) return;
		const self = this;
		this._originalFetch = window.fetch;
		window.fetch = async function (input, init) {
			let url = '';
			let method = 'GET';
			let headers = {};
			let body = '';
			let contentType = '';

			if (input instanceof Request) {
				url = input.url;
				method = input.method || 'GET';
				headers = headersToObject(input.headers);
				contentType = headers['Content-Type'] || headers['content-type'] || '';
				try {
					body = await input.clone().text();
				} catch (_) {}
			} else {
				url = typeof input === 'string' ? input : (input && input.url) ? input.url : '';
				const opts = init || {};
				method = (opts.method || 'GET').toUpperCase();
				headers = headersToObject(opts.headers);
				contentType = headers['Content-Type'] || headers['content-type'] || '';
				body = normalizeBody(opts.body, contentType);
			}
			
			if (self._isAdrEndpoint(url)) {
				return self._originalFetch.apply(this, arguments);
			}

			const result = self.detector.analyzeRequest({ url, method, headers, body, contentType });
			if (result.detected) self.report(result, url, method, 'fetch');

			const currentOrigin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
			const ssrfResult = self.ssrfDetector.analyze({ url, body, currentHostname: (typeof window !== 'undefined' && window.location) ? window.location.hostname : '' });
			if (ssrfResult.detected) self.report(ssrfResult, url, method, 'fetch');

			const redirectResult = self.openRedirectDetector.analyze(url, currentOrigin);
			if (redirectResult.detected) self.report(redirectResult, url, method, 'fetch');

			const nosqlResult = self.nosqlDetector.analyze({ url, body });
			if (nosqlResult.detected) self.report(nosqlResult, url, method, 'fetch');

			const protoResult = self.protoPollutionDetector.analyze({ url, body });
			if (protoResult.detected) self.report(protoResult, url, method, 'fetch');

			return self._originalFetch.apply(this, arguments);
		};
	}

	restoreFetch() {
		if (this._originalFetch && typeof window !== 'undefined') {
			window.fetch = this._originalFetch;
			this._originalFetch = null;
		}
	}

	patchXHR() {
		if (typeof XMLHttpRequest === 'undefined' || this._originalXHROpen) return;
		const self = this;
		this._originalXHROpen = XMLHttpRequest.prototype.open;
		this._originalXHRSend = XMLHttpRequest.prototype.send;
		this._originalXHRSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

		XMLHttpRequest.prototype.open = function (method, url) {
			this._adrInjection = { method: (method || 'GET').toUpperCase(), url: url || '', headers: {} };
			return self._originalXHROpen.apply(this, arguments);
		};

		XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
			if (this._adrInjection && this._adrInjection.headers) {
				this._adrInjection.headers[name] = value;
			}
			return self._originalXHRSetRequestHeader.apply(this, arguments);
		};

		XMLHttpRequest.prototype.send = function (body) {
			const cap = this._adrInjection;
			if (cap && self.transport) {
				if (self._isAdrEndpoint(cap.url)) {
					return self._originalXHRSend.apply(this, arguments);
				}
				const contentType = (cap.headers && (cap.headers['Content-Type'] || cap.headers['content-type'])) || '';
				const bodyStr = normalizeBody(body, contentType);
				const result = self.detector.analyzeRequest({
					url: cap.url,
					method: cap.method,
					headers: cap.headers,
					body: bodyStr,
					contentType
				});
				if (result.detected) self.report(result, cap.url, cap.method, 'xhr');

				const currentOrigin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
				const ssrfResult = self.ssrfDetector.analyze({ url: cap.url, body: bodyStr, currentHostname: (typeof window !== 'undefined' && window.location) ? window.location.hostname : '' });
				if (ssrfResult.detected) self.report(ssrfResult, cap.url, cap.method, 'xhr');

				const redirectResult = self.openRedirectDetector.analyze(cap.url, currentOrigin);
				if (redirectResult.detected) self.report(redirectResult, cap.url, cap.method, 'xhr');

				const nosqlResult = self.nosqlDetector.analyze({ url: cap.url, body: bodyStr });
				if (nosqlResult.detected) self.report(nosqlResult, cap.url, cap.method, 'xhr');

				const protoResult = self.protoPollutionDetector.analyze({ url: cap.url, body: bodyStr });
				if (protoResult.detected) self.report(protoResult, cap.url, cap.method, 'xhr');
			}
			return self._originalXHRSend.apply(this, arguments);
		};
	}

	restoreXHR() {
		if (this._originalXHROpen && typeof XMLHttpRequest !== 'undefined') {
			XMLHttpRequest.prototype.open = this._originalXHROpen;
			XMLHttpRequest.prototype.send = this._originalXHRSend;
			XMLHttpRequest.prototype.setRequestHeader = this._originalXHRSetRequestHeader;
			this._originalXHROpen = null;
			this._originalXHRSend = null;
			this._originalXHRSetRequestHeader = null;
		}
	}

	attachFormSubmit() {
		if (typeof document === 'undefined' || this._submitBound) return;
		const self = this;
		this._submitBound = function (e) {
			const form = e.target;
			if (!form || form.nodeName !== 'FORM') return;
			const url = (form.action && form.action.trim()) ? form.action : (window.location && window.location.href) || '';
			const method = (form.method || 'GET').toUpperCase();

			let fd = null;
			try {
				fd = new FormData(form);
			} catch (_) {}

			// Analyze each field's own value individually — evidence must be the
			// complete value of the field that triggered detection, not a position
			// inside a synthetic "name=value&name=value" string built by joining
			// every field together first. That concatenation let a pattern match
			// span the literal "&" between two unrelated fields (e.g. "--" at the
			// end of one field's value plus the next field's separator produced
			// evidence like "--&" that was never actually submitted by the user).
			if (fd) {
				for (const [, rawValue] of fd.entries()) {
					const value = typeof rawValue === 'string' ? rawValue : (rawValue && rawValue.name) ? rawValue.name : '';
					if (!value) continue;

					const result = self.detector.analyzeRequest({
						url,
						method,
						headers: {},
						body: value,
						contentType: 'application/x-www-form-urlencoded'
					});
					if (result.detected) {
						self.report(result, url, method, 'submit');
						break;
					}
				}
			}

			const currentOrigin = (window.location && window.location.origin) ? window.location.origin : '';
			const redirectResult = self.openRedirectDetector.analyze(url, currentOrigin);
			if (redirectResult.detected) self.report(redirectResult, url, method, 'submit');
		};
		document.addEventListener('submit', this._submitBound, true);
	}

	detachFormSubmit() {
		if (this._submitBound && typeof document !== 'undefined') {
			document.removeEventListener('submit', this._submitBound, true);
			this._submitBound = null;
		}
	}
}

export default InjectionMonitor;
